import api from './api';
import {
  Exam,
  CreateExamDto,
  UpdateExamDto,
  CreateQuestionDto,
  UpdateQuestionDto,
  SaveAnswerDto,
  SubmitExamDto,
  Question,
  ExamAttempt,
  ExamAnalytics,
  TeacherCertificationStatus,
  PublicDashboardExam,
  GuestExamAccess,
  GuestExamAttemptResult,
  StudentExamCertificate,
} from '../types/exam';

export const examApi = {
  // Exam Management - Teacher
  createExam: async (data: CreateExamDto): Promise<Exam> => {
    const response = await api.post('/exams', data);
    return response.data;
  },

  updateExam: async (examId: string, data: UpdateExamDto): Promise<Exam> => {
    const response = await api.put(`/exams/${examId}`, data);
    return response.data;
  },

  deleteExam: async (examId: string): Promise<void> => {
    await api.delete(`/exams/${examId}`);
  },

  getExamById: async (examId: string): Promise<Exam> => {
    const response = await api.get(`/exams/${examId}`);
    return response.data;
  },

  getTeacherExams: async (): Promise<Exam[]> => {
    const response = await api.get('/exams');
    return response.data;
  },

  getAvailableExams: async (): Promise<Exam[]> => {
    const response = await api.get('/exams');
    return response.data;
  },

  getPublicDashboardExams: async (category?: string): Promise<PublicDashboardExam[]> => {
    const response = await api.get('/public/exams-dashboard', {
      params: category ? { category } : undefined,
    });
    return response.data;
  },

  getPublicDashboardExamById: async (examId: string): Promise<PublicDashboardExam> => {
    const response = await api.get(`/public/exams-dashboard/${examId}`);
    return response.data;
  },

  createSchoolGuestAccess: async (
    examId: string,
    data: { accessCode: string; guestName: string; guestEmail?: string; guestPhone?: string },
  ): Promise<GuestExamAccess> => {
    const response = await api.post(`/public/exams-dashboard/${examId}/access/school`, data);
    return response.data;
  },

  createReferralInvite: async (
    examId: string,
  ): Promise<{ guestAccessId: string; inviteCode: string; shareUrl: string; amount: number; currency: string }> => {
    const response = await api.post(`/exams/${examId}/referral-invites`);
    return response.data;
  },

  initiateReferralGuestPayment: async (
    inviteCode: string,
    data: { guestName: string; guestEmail?: string; guestPhone?: string },
  ): Promise<GuestExamAccess & { paymentLinkId?: string; checkoutUrl?: string; amount?: number; currency?: string }> => {
    const response = await api.post(`/public/exams-dashboard/referral-invites/${inviteCode}/initiate-payment`, data);
    return response.data;
  },

  verifyReferralGuestPayment: async (
    inviteCode: string,
    paymentLinkId?: string,
  ): Promise<GuestExamAccess> => {
    const response = await api.post(`/public/exams-dashboard/referral-invites/${inviteCode}/verify-payment`, {
      paymentLinkId,
    });
    return response.data;
  },

  startGuestAttempt: async (examId: string, accessToken: string): Promise<GuestExamAttemptResult> => {
    const response = await api.post(`/public/exams-dashboard/${examId}/guest-attempts/start`, { accessToken });
    return response.data;
  },

  getGuestAttemptQuestions: async (attemptId: string, accessToken: string): Promise<Question[]> => {
    const response = await api.get(`/public/exams-dashboard/guest-attempts/${attemptId}/questions`, {
      params: { accessToken },
    });
    return response.data;
  },

  saveGuestAttemptAnswer: async (
    attemptId: string,
    accessToken: string,
    data: SaveAnswerDto,
  ): Promise<GuestExamAttemptResult> => {
    const response = await api.post(`/public/exams-dashboard/guest-attempts/${attemptId}/answers`, {
      ...data,
      accessToken,
    });
    return response.data;
  },

  submitGuestAttempt: async (
    attemptId: string,
    accessToken: string,
    data: SubmitExamDto,
  ): Promise<GuestExamAttemptResult> => {
    const response = await api.post(`/public/exams-dashboard/guest-attempts/${attemptId}/submit`, {
      ...data,
      accessToken,
    });
    return response.data;
  },

  getGuestAttemptResult: async (attemptId: string, accessToken: string): Promise<GuestExamAttemptResult> => {
    const response = await api.get(`/public/exams-dashboard/guest-attempts/${attemptId}/result`, {
      params: { accessToken },
    });
    return response.data;
  },

  // Exam Lifecycle
  publishExam: async (examId: string): Promise<Exam> => {
    const response = await api.post(`/exams/${examId}/publish`);
    return response.data;
  },

  closeExam: async (examId: string): Promise<Exam> => {
    const response = await api.post(`/exams/${examId}/close`);
    return response.data;
  },

  publishExamResults: async (examId: string): Promise<Exam> => {
    const response = await api.post(`/exams/${examId}/results/publish`);
    return response.data;
  },

  archiveExam: async (examId: string): Promise<Exam> => {
    const response = await api.post(`/exams/${examId}/archive`);
    return response.data;
  },

  // Question Management
  addQuestion: async (examId: string, data: CreateQuestionDto): Promise<Question> => {
    const response = await api.post(`/exams/${examId}/questions`, data);
    return response.data;
  },

  getExamQuestions: async (examId: string): Promise<Question[]> => {
    const response = await api.get(`/exams/${examId}/questions`);
    return response.data;
  },

  updateQuestion: async (
    examId: string,
    questionId: string,
    data: UpdateQuestionDto
  ): Promise<Question> => {
    const response = await api.put(`/exams/${examId}/questions/${questionId}`, data);
    return response.data;
  },

  deleteQuestion: async (examId: string, questionId: string): Promise<void> => {
    await api.delete(`/exams/${examId}/questions/${questionId}`);
  },

  // Exam Analytics
  getExamAnalytics: async (examId: string): Promise<ExamAnalytics> => {
    const response = await api.get(`/exams/${examId}/analytics`);
    return response.data;
  },

  // Student Exam Attempts
  startExam: async (examId: string): Promise<ExamAttempt> => {
    const response = await api.post(`/exams/${examId}/attempts/start`);
    return response.data;
  },

  saveAnswer: async (attemptId: string, data: SaveAnswerDto): Promise<void> => {
    await api.post(`/exams/attempts/${attemptId}/answers`, data);
  },

  submitExam: async (attemptId: string, data: SubmitExamDto): Promise<ExamAttempt> => {
    const response = await api.post(`/exams/attempts/${attemptId}/submit`, data);
    return response.data;
  },

  getExamAttempt: async (attemptId: string): Promise<ExamAttempt> => {
    const response = await api.get(`/exams/attempts/${attemptId}`);
    return response.data;
  },

  getStudentAttempts: async (examId: string): Promise<ExamAttempt[]> => {
    const response = await api.get(`/exams/${examId}/attempts`);
    return response.data;
  },

  getAttemptResult: async (attemptId: string): Promise<ExamAttempt> => {
    const response = await api.get(`/exams/attempts/${attemptId}/result`);
    return response.data;
  },

  getStudentCertificates: async (): Promise<StudentExamCertificate[]> => {
    const response = await api.get('/exams/certificates/me');
    return response.data;
  },

  // Teacher certification flow
  getTeacherCertificationStatus: async (): Promise<TeacherCertificationStatus> => {
    const response = await api.get('/exams/teacher-certification/status');
    return response.data;
  },

  startTeacherCertificationAttempt: async (): Promise<ExamAttempt> => {
    const response = await api.post('/exams/teacher-certification/attempts/start');
    return response.data;
  },

  getTeacherCertificationQuestions: async (attemptId: string): Promise<Question[]> => {
    const response = await api.get(`/exams/teacher-certification/attempts/${attemptId}/questions`);
    return response.data;
  },

  saveTeacherCertificationAnswer: async (attemptId: string, data: SaveAnswerDto): Promise<ExamAttempt> => {
    const response = await api.post(`/exams/teacher-certification/attempts/${attemptId}/answers`, data);
    return response.data;
  },

  submitTeacherCertificationAttempt: async (
    attemptId: string,
    data: SubmitExamDto,
  ): Promise<ExamAttempt> => {
    const response = await api.post(`/exams/teacher-certification/attempts/${attemptId}/submit`, data);
    return response.data;
  },
};

export default examApi;
